import React, { useMemo } from 'react';
import CommonSider from '@/components/Sider/Sider';
import { MenuProps, Skeleton } from 'antd';
import {
	ExclamationCircleOutlined,
	BookOutlined,
	UserOutlined,
	DashboardOutlined,
	KeyOutlined,
	ApiOutlined,
	DollarOutlined,
	CustomerServiceOutlined,
	SettingOutlined,
	MailOutlined,
	PieChartOutlined,
	CreditCardOutlined,
} from '@ant-design/icons';
import './Sidebar.scss';
import MobileLogo from '@/assets/images/mobile-log.png';
import DesktopLogo from '@/assets/images/desktop-logo.png';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { getResellerData } from '@/store/reseller/selector';
import { getResellerLogo } from '@/api/unbrandedReceptionist';
import { useQuery } from '@tanstack/react-query';
import { UserDashboardSiderItem } from 'src/enums/EUserDashboardSiderItem';
import LocalStorageUtility from '@/utils/localStorage';
import { resetTranslationCookies } from '@/utils/translation';

interface SidebarProps {
	onMenuSelect: (key: UserDashboardSiderItem) => void;
	activeMenuItem: string;
	menuRef: React.RefObject<HTMLDivElement>;
	className?: string;
	debrand: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
	activeMenuItem,
	onMenuSelect,
	menuRef,
	className = '',
	debrand,
}) => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const { isMobile } = useMobileDeviceCheck();
	const currentReseller = useAppSelector(getResellerData);

	const menuItems: MenuProps['items'] = [
		{
			key: UserDashboardSiderItem.DASHBOARD,
			icon: <DashboardOutlined />,
			label: UserDashboardSiderItem.DASHBOARD,
		},
		{
			key: UserDashboardSiderItem.CONTACT_SUPPORT,
			icon: <CustomerServiceOutlined />,
			label: UserDashboardSiderItem.CONTACT_SUPPORT,
		},
		{
			key: UserDashboardSiderItem.ACCOUNT,
			icon: <UserOutlined />,
			label: UserDashboardSiderItem.ACCOUNT,
			children: [
				{
					key: UserDashboardSiderItem.SETTINGS,
					icon: <SettingOutlined />,
					label: UserDashboardSiderItem.SETTINGS,
				},
				{
					key: UserDashboardSiderItem.PASSWORD,
					icon: <KeyOutlined />,
					label: UserDashboardSiderItem.PASSWORD,
				},
				{
					key: UserDashboardSiderItem.SUBSCRIPTION,
					icon: <CreditCardOutlined />,
					label: UserDashboardSiderItem.SUBSCRIPTION,
				},
				{
					key: UserDashboardSiderItem.USAGE,
					icon: <PieChartOutlined />,
					label: UserDashboardSiderItem.USAGE,
				},
				{
					key: UserDashboardSiderItem.CREDITS,
					icon: <DollarOutlined />,
					label: UserDashboardSiderItem.CREDITS,
				},
				{
					key: UserDashboardSiderItem.OUTBOUND_SETTINGS,
					icon: <SettingOutlined />,
					label: UserDashboardSiderItem.OUTBOUND_SETTINGS,
					children: [
						{
							key: UserDashboardSiderItem.OUTBOUND_BYOT,
							icon: <ApiOutlined />,
							label: 'Bring Your Twilio',
						},
						{
							key: UserDashboardSiderItem.OUTBOUND_EMAIL,
							icon: <MailOutlined />,
							label: 'Email Configuration',
						},
					],
				},
			],
		},
	];

	// Memoize support buttons
	const supportButtons = useMemo(() => {
		const buttons = [];

		if (currentReseller?.customButton1Label && currentReseller?.customButton1URL) {
			buttons.push({
				key: UserDashboardSiderItem.RESELLER_SUPPORT_BUTTON_1,
				icon: <ExclamationCircleOutlined />,
				label: currentReseller?.customButton1Label,
			});
		}

		if (currentReseller?.customButton2Label && currentReseller?.customButton2URL) {
			buttons.push({
				key: UserDashboardSiderItem.RESELLER_SUPPORT_BUTTON_2,
				icon: <BookOutlined />,
				label: currentReseller?.customButton2Label,
			});
		}

		return buttons;
	}, [currentReseller]);

	const getDynamicSidebarItems = () => {
		let items = menuItems;
		if (loggedInUser.userType === 'SubAccount') {
			// Filtering out items for sub-accounts
			items = items.filter(
				(item) =>
					item!.key !== UserDashboardSiderItem.SUBSCRIPTION &&
					item!.key !== UserDashboardSiderItem.USAGE &&
					item!.key !== UserDashboardSiderItem.ACCOUNT &&
					item!.key !== UserDashboardSiderItem.CREDITS,
			);

			// adding support buttons if any
			items = items.concat(supportButtons);
		}

		return items;
	};

	const { data, isPending: isLogoLoading } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller?.logo as string }),
		enabled: !!(debrand && currentReseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!data) {
			return;
		}
		return URL.createObjectURL(data);
	}, [data]);

	const handleClick: MenuProps['onClick'] = (e) => {
		if (e.key === 'Logout') {
			LocalStorageUtility.clearLocalData();
			resetTranslationCookies();
		} else {
			onMenuSelect(e.key as UserDashboardSiderItem);

			// Handle reseller support buttons
			if (e.key === UserDashboardSiderItem.RESELLER_SUPPORT_BUTTON_1) {
				window.open(currentReseller?.customButton1URL, '_blank');
			}

			if (e.key === UserDashboardSiderItem.RESELLER_SUPPORT_BUTTON_2) {
				window.open(currentReseller?.customButton2URL, '_blank');
			}
		}
	};

	const baseClass = 'sider-container';
	const logo = (
		<div className={`${baseClass}_logo`}>
			{debrand ? (
				<div>
					{isLogoLoading ? (
						<Skeleton.Avatar active size={80} shape="circle" />
					) : resellerLogoUrl ? (
						<div style={{ display: 'flex', justifyContent: 'center' }}>
							<img src={resellerLogoUrl} alt="logo" width={80} height={80} />
						</div>
					) : (
						<h1>{currentReseller?.businessName}</h1>
					)}
				</div>
			) : isMobile ? (
				<img src={MobileLogo} alt="logo" height={80} width={80} />
			) : (
				<img src={DesktopLogo} alt="logo" />
			)}
		</div>
	);

	return (
		<CommonSider
			items={getDynamicSidebarItems()}
			logo={logo}
			activeMenuItem={activeMenuItem}
			handleClick={handleClick}
			className={baseClass + className}
			menuRef={menuRef}
			debrand={debrand}
			width={250}
		/>
	);
};

export default Sidebar;
